import { api } from '../api/client';
import { hours, useMyAttendance, useMyRoster, useRosterMutation } from '../api/attendance';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../format';
import { Badge, Button, ErrorNote, Loading, useReport, useToast } from '../ui/kit';

const timeOf = (iso: string, tz: string): string => new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));

/** Clock in and out, today's shift, and the days ahead. Shown to every member of staff who has a staff record. */
export function ClockCard() {
  const { me } = useAuth();
  const tz = me?.property.timezone ?? 'Africa/Accra';
  const q = useMyAttendance();
  const mine = useMyRoster();
  const toast = useToast();
  const report = useReport();
  const clock = useRosterMutation((what: 'clock-in' | 'clock-out') => api(`/attendance/${what}`, { method: 'POST', body: {} }));

  if (q.isLoading) return <Loading rows={1} />;
  if (q.error) return <ErrorNote error={q.error} retry={() => void q.refetch()} />;
  if (!q.data?.linked) return null; // no staff record: the page already says so

  const d = q.data;
  const open = d.open;
  return (
    <section className="card stack" aria-labelledby="clock">
      <div className="card-head">
        <h2 className="h2" id="clock">Clock in</h2>
        {open ? <Badge tone="good">In since {timeOf(open.clockIn, tz)}</Badge> : <Badge>Not clocked in</Badge>}
      </div>
      <p className="small muted">{d.shiftToday ? `Your shift today: ${d.shiftToday.startTime} – ${d.shiftToday.endTime}${d.shiftToday.note ? ` (${d.shiftToday.note})` : ''}` : 'You are not on the roster today.'}</p>
      {open ? (
        <Button variant="primary" block loading={clock.isPending} onClick={() => clock.mutate('clock-out', { onError: report, onSuccess: () => toast.ok('Clocked out') })}>Clock out</Button>
      ) : (
        <Button variant="primary" block loading={clock.isPending} onClick={() => clock.mutate('clock-in', { onError: report, onSuccess: () => toast.ok('Clocked in') })}>Clock in</Button>
      )}
      {(mine.data?.shifts ?? []).length > 0 ? (
        <div className="stack">
          <div className="eyebrow">Your next shifts</div>
          {mine.data!.shifts.slice(0, 5).map((s) => (
            <div key={s.date} className="row small" style={{ justifyContent: 'space-between' }}>
              <span>{formatDate(s.date)}</span>
              <span className="num">{s.startTime} – {s.endTime}</span>
            </div>
          ))}
        </div>
      ) : null}
      {(d.recent ?? []).length > 0 ? (
        <div className="stack">
          <div className="eyebrow">Recent</div>
          {d.recent!.slice(0, 4).map((r) => (
            <div key={r.id} className="small muted">
              {formatDate(r.date)} · {timeOf(r.clockIn, tz)} – {r.clockOut ? timeOf(r.clockOut, tz) : '…'}
              {r.lateMinutes > 0 ? ` · ${hours(r.lateMinutes)} late` : ''}
              {r.overtimeApprovedMinutes > 0 ? ` · ${hours(r.overtimeApprovedMinutes)} overtime approved` : ''}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
